import { useEffect, useState } from 'react';
import { createResource,getResources,deleteResource, updateResource} from '../../../router/api';
import toast from 'react-hot-toast';

interface ConvertToBCCModalProps {
  isOpen: boolean;
  onClose: () => void;
  quote: any;
  onConverted: () => void;
}

export default function ConvertToBCCModal({
  isOpen,
  onClose,
  quote,
  onConverted,
}: ConvertToBCCModalProps) {
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(false);
  const [checkResult, setCheckResult] = useState<any>(null);

  const [deliveryDate, setDeliveryDate] = useState('');
  const [notes, setNotes] = useState('');
  const [conditionsPaiement, setConditionsPaiement] = useState('');

  /* ===================== INIT ===================== */
  useEffect(() => {
    if (!isOpen || !quote) return;

    setChecking(true);

    getResources(`devis-bcc/${quote.id}/check-conversion`)
      .then(res => {
        setCheckResult(res.data);
        setConditionsPaiement(
          res.data?.credit?.conditions_paiement ||
          quote.client?.conditions_paiement ||
          'Paiement à 30 jours'
        );
      })
      .catch(() => toast.error('Erreur lors de la vérification'))
      .finally(() => setChecking(false));

    setDeliveryDate(new Date().toISOString().split('T')[0]);
    setNotes('');
  }, [isOpen, quote]);

  if (!isOpen || !quote) return null;

  const creditExceeded = Boolean(checkResult?.credit?.exceeded);

  /* ===================== CONFIRM ===================== */
  const handleConfirm = async (force = false) => {
    setLoading(true);

    try {
      await createResource(`devis-bcc/${quote.id}/convert-to-bcc`, {
        delivery_date: deliveryDate,
        notes,
        conditions_paiement: conditionsPaiement,
        force,
      });

      toast.success('Bon de Commande Client créé avec succès');
      onConverted();
      onClose();
    } catch (e: any) {
      toast.error(e.response?.data?.message || 'Erreur lors de la conversion');
    } finally {
      setLoading(false);
    }
  };

  /* ===================== RENDER ===================== */
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white w-full max-w-2xl rounded-xl shadow-lg">

        {/* Header */}
        <div className="px-6 py-4 border-b flex justify-between items-center">
          <h3 className="text-lg font-semibold">
            Conversion en Bon de Commande Client
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <i className="ri-close-line text-xl" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-4 space-y-5">

          {/* Récap devis */}
          <div className="bg-gray-50 p-4 rounded-lg text-sm space-y-1">
            <div><strong>Devis :</strong> {quote.numero}</div>
            <div><strong>Client :</strong> {quote.client?.raison_sociale}</div>
            <div><strong>Montant TTC :</strong> {Number(quote.total_ttc).toLocaleString()} €</div>
            <div><strong>Lignes :</strong> {quote.lignes?.length ?? 0}</div>
          </div>

          {/* Vérifications */}
          {checking && (
            <div className="text-center text-sm text-gray-500">
              Vérification en cours…
            </div>
          )}

          {!checking && checkResult && (
            <div className="space-y-3">

              {/* Stock */}
              <div
                className={`p-3 rounded border ${
                  checkResult.stock?.ok
                    ? 'bg-green-50 border-green-200'
                    : 'bg-orange-50 border-orange-200'
                }`}
              >
                <strong>Stock :</strong>{' '}
                {checkResult.stock?.ok
                  ? 'OK'
                  : 'Attention – ruptures détectées'}
              </div>

              {/* Crédit */}
              <div
                className={`p-3 rounded border ${
                  creditExceeded
                    ? 'bg-red-50 border-red-200'
                    : 'bg-green-50 border-green-200'
                }`}
              >
                <strong>Crédit client :</strong>
                <div className="text-sm mt-1">
                  Encours : {checkResult.credit.encours.toLocaleString()} €<br />
                  Plafond : {checkResult.credit.plafond.toLocaleString()} €<br />
                  Après commande : {checkResult.credit.after.toLocaleString()} €
                </div>
              </div>
            </div>
          )}

          {/* Paramètres BCC */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium">
                Date de livraison prévue
              </label>
              <input
                type="date"
                value={deliveryDate}
                onChange={e => setDeliveryDate(e.target.value)}
                className="mt-1 w-full border rounded-md px-3 py-2"
              />
            </div>

            <div>
              <label className="block text-sm font-medium">
                Conditions de paiement
              </label>
              <input
                type="text"
                value={conditionsPaiement}
                onChange={e => setConditionsPaiement(e.target.value)}
                placeholder="Ex : Paiement à 30 jours"
                className="mt-1 w-full border rounded-md px-3 py-2"
              />
            </div>

            <div>
              <label className="block text-sm font-medium">
                Notes internes
              </label>
              <textarea
                rows={3}
                value={notes}
                onChange={e => setNotes(e.target.value)}
                className="mt-1 w-full border rounded-md px-3 py-2"
              />
            </div>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 p-3 rounded text-sm text-yellow-700">
            ⚠️ Cette action créera un Bon de Commande Client et bloquera définitivement le devis.
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t flex justify-end gap-3">
          <button
            onClick={onClose}
            disabled={loading}
            className="px-4 py-2 border rounded-md text-sm"
          >
            Annuler
          </button>

          {!creditExceeded && (
            <button
              onClick={() => handleConfirm(false)}
              disabled={loading}
              className="px-4 py-2 rounded-md bg-green-600 text-white text-sm hover:bg-green-700"
            >
              Valider le BCC
            </button>
          )}

          {creditExceeded && (
            <button
              onClick={() => handleConfirm(true)}
              disabled={loading}
              className="px-4 py-2 rounded-md bg-red-600 text-white text-sm hover:bg-red-700"
            >
              Forcer la validation
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
